import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { Plus, Package, ArrowLeft, Filter, ChevronDown, ChevronRight, X, ImageIcon, Eye, Edit, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import ViewToggle from '@/components/shared/ViewToggle';
import FilterToolbar from '@/components/shared/FilterToolbar';
import { useViewPreference } from '@/hooks/useViewPreference';
import { useLineItemDelete } from '@/components/internal/line-items/hooks/useLineItemDelete';
import { useInternalLineItemFilterBuilder } from '@/pages/internal-line-items/hooks/useInternalLineItemFilterBuilder';
import { FilterBuilder } from '@/components/invoices/FilterBuilder';
import EntityActions from '@/components/shared/EntityActions';
import { formatCurrency, cn } from '@/lib/utils';
import { getUnitLabel } from '@/types/unit-types';
import LineItemDetailsDialog from '@/components/internal/line-items/LineItemDetailsDialog';

interface BrowseCat {
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  display_order: number;
}

const BrowseCategoryNode = ({
  category,
  allCategories,
  selectedId,
  onSelect,
  depth,
}: {
  category: BrowseCat;
  allCategories: BrowseCat[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  depth: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const children = allCategories.filter((c) => c.parent_id === category.id);
  const hasChildren = children.length > 0;
  const isSelected = selectedId === category.id;

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-muted ${
          isSelected ? 'bg-primary/10 text-primary font-medium' : ''
        }`}
        style={{ paddingLeft: `${depth * 12 + 8}px` }}
        onClick={() => onSelect(isSelected ? null : category.id)}
      >
        {hasChildren ? (
          <button
            type="button"
            className="p-0.5 hover:bg-muted-foreground/10 rounded"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className="truncate">{category.name}</span>
      </div>
      {hasChildren && expanded && (
        <div>
          {children
            .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
            .map((child) => (
              <BrowseCategoryNode
                key={child.id}
                category={child}
                allCategories={allCategories}
                selectedId={selectedId}
                onSelect={onSelect}
                depth={depth + 1}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default function SupplierLineItemsPage() {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth() as any;
  const companyId = profile?.company_id ?? null;

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const { viewMode, setViewMode } = useViewPreference({
    entityType: 'supplier-line-items',
    defaultView: 'list',
  });
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const effectiveViewMode = windowWidth < 1024 ? 'grid' : viewMode;
  const isMobileBrowse = windowWidth < 1024;

  // Browse panel state
  const [browsePanelOpen, setBrowsePanelOpen] = useState(false);
  const [selectedBrowseCategory, setSelectedBrowseCategory] = useState<string | null>(null);

  const supplierQuery = useQuery({
    queryKey: ['supplier', supplierId],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('id', supplierId!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const itemsQuery = useQuery({
    queryKey: ['supplier-line-items', supplierId, companyId],
    enabled: !!supplierId && !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('internal_line_item_price_sources')
        .select(`
          id,
          last_seen_price,
          last_checked_at,
          url,
          internal_line_items!internal_line_item_id(
            id, title, description, base_unit, base_cost,
            final_price, markup_percentage, image_urls,
            is_active, updated_at, category_id, browse_category_id,
            company_id
          )
        `)
        .eq('supplier_id', supplierId!)
        .eq('internal_line_items.company_id', companyId!)
        .eq('internal_line_items.is_active', true);
      if (error) throw error;
      return ((data ?? []) as any[])
        .filter((row) => !!row.internal_line_items)
        .map((row) => ({
          source_id: row.id,
          last_seen_price: row.last_seen_price,
          last_checked_at: row.last_checked_at,
          url: row.url,
          ...row.internal_line_items,
          item_type: 'internal' as const,
          name: row.internal_line_items.title ?? row.internal_line_items.description,
          category_name: row.internal_line_items.category ?? null,
          trade_name: row.internal_line_items.trade ?? null,
          internal_pricing_tiers: [],
        }));
    },
  });

  const { data: browseCategories } = useQuery({
    queryKey: ['browse_categories_global'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('browse_categories')
        .select('id, name, parent_id, depth, display_order')
        .is('company_id', null)
        .order('depth', { ascending: true })
        .order('display_order', { ascending: true });
      if (error) throw error;
      return (data ?? []) as BrowseCat[];
    },
    staleTime: 10 * 60 * 1000,
  });

  const { handleDelete } = useLineItemDelete(() => itemsQuery.refetch());

  // Filter by selected browse category (include descendants)
  const browseFilteredItems = useMemo(() => {
    const items = itemsQuery.data ?? [];
    if (!selectedBrowseCategory) return items;
    const all = browseCategories ?? [];
    const allowed = new Set<string>([selectedBrowseCategory]);
    let added = true;
    while (added) {
      added = false;
      for (const c of all) {
        if (c.parent_id && allowed.has(c.parent_id) && !allowed.has(c.id)) {
          allowed.add(c.id);
          added = true;
        }
      }
    }
    return items.filter((it) => it.browse_category_id && allowed.has(it.browse_category_id));
  }, [itemsQuery.data, selectedBrowseCategory, browseCategories]);

  const filterBuilder = useInternalLineItemFilterBuilder(browseFilteredItems as any);

  const filteredItems = useMemo(() => {
    const items = filterBuilder.filteredLineItems ?? [];
    const q = search.trim().toLowerCase();
    const searched = q
      ? items.filter(
          (it: any) =>
            ((it.title ?? it.description) ?? '').toLowerCase().includes(q)
        )
      : items;

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...searched].sort((a: any, b: any) => {
      const get = (it: any) => {
        switch (sortBy) {
          case 'final_price':
            return Number(it.last_seen_price ?? it.base_cost ?? 0);
          case 'base_cost':
            return Number(it.base_cost ?? 0);
          case 'updated_at':
            return new Date(it.updated_at ?? 0).getTime();
          case 'name':
          default:
            return (it.title ?? '').toLowerCase();
        }
      };
      const av = get(a);
      const bv = get(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filterBuilder.filteredLineItems, search, sortBy, sortDir]);

  const handleBrowseCategorySelect = (id: string | null) => {
    setSelectedBrowseCategory(id);
    if (isMobileBrowse) setBrowsePanelOpen(false);
  };

  const getActiveFilterCount = () =>
    filterBuilder.appliedGroups.reduce((count, group) => count + group.rules.length, 0);

  const selectedBrowseCategoryName = selectedBrowseCategory
    ? (browseCategories ?? []).find((c) => c.id === selectedBrowseCategory)?.name ?? null
    : null;

  const supplier = supplierQuery.data;
  const createHref = `/cost-center/internal-line-items/create?supplierId=${supplierId}`;

  return (
    <PageLayout>
      <PageHeader
        title={supplier?.name ? `${supplier.name} — Line Items` : 'Supplier Line Items'}
        description="Items associated with this supplier"
      />

      <Card>
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/cost-center/suppliers')}
              className="h-9"
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to Suppliers</span>
            </Button>
            {windowWidth >= 1024 && (
              <ViewToggle value={effectiveViewMode} onChange={setViewMode} />
            )}
          </div>

          <FilterToolbar
            actions={
              <Button onClick={() => navigate(createHref)} className="h-9">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Line Item</span>
              </Button>
            }
            leadingFilterAction={
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBrowsePanelOpen((prev) => !prev)}
                className="h-9 shrink-0 gap-1.5"
                aria-label="Browse categories"
              >
                <Filter className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Browse</span>
              </Button>
            }
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search line items..."
            sortOptions={[
              { value: 'name', label: 'Name' },
              { value: 'final_price', label: 'Final Price' },
              { value: 'base_cost', label: 'Base Cost' },
              { value: 'updated_at', label: 'Date Updated' },
            ]}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            advancedFilter={
              <FilterBuilder
                draftGroups={filterBuilder.draftGroups}
                appliedGroups={filterBuilder.appliedGroups}
                fields={filterBuilder.filterFields}
                entities={filterBuilder.filterEntities}
                attributesByEntity={filterBuilder.filterAttributesByEntity}
                onAddGroup={filterBuilder.addGroup}
                onUpdateGroup={filterBuilder.updateGroup}
                onRemoveGroup={filterBuilder.removeGroup}
                onAddRuleToGroup={filterBuilder.addRuleToGroup}
                onUpdateRuleInGroup={filterBuilder.updateRuleInGroup}
                onRemoveRuleFromGroup={filterBuilder.removeRuleFromGroup}
                onClearAll={filterBuilder.clearAllRules}
                onApplyFilters={filterBuilder.applyFilters}
                hasActiveFilters={filterBuilder.hasActiveFilters}
                hasUnappliedChanges={filterBuilder.hasUnappliedChanges}
                canApplyFilters={filterBuilder.canApplyFilters}
              />
            }
            hasAdvancedFilters={filterBuilder.hasActiveFilters}
            advancedFilterCount={getActiveFilterCount()}
            onClearAdvancedFilters={filterBuilder.clearAllRules}
          />

          {selectedBrowseCategoryName && (
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="gap-1 pr-1 h-7">
                <span className="truncate max-w-[10rem]">{selectedBrowseCategoryName}</span>
                <button
                  type="button"
                  onClick={() => handleBrowseCategorySelect(null)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                  aria-label="Clear browse category"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            </div>
          )}
        </CardHeader>

        <div className="flex gap-0">
          {(() => {
            const pickerBody = (
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Filter className="h-4 w-4" />
                    Browse
                  </div>
                  {selectedBrowseCategory && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => handleBrowseCategorySelect(null)}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <div
                  className={`px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-muted ${
                    !selectedBrowseCategory ? 'bg-primary/10 text-primary font-medium' : ''
                  }`}
                  onClick={() => handleBrowseCategorySelect(null)}
                >
                  All
                </div>
                {(browseCategories ?? [])
                  .filter((c) => !c.parent_id)
                  .sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name))
                  .map((root) => (
                    <BrowseCategoryNode
                      key={root.id}
                      category={root}
                      allCategories={browseCategories ?? []}
                      selectedId={selectedBrowseCategory}
                      onSelect={handleBrowseCategorySelect}
                      depth={0}
                    />
                  ))}
              </div>
            );

            if (isMobileBrowse) {
              return (
                <Drawer open={browsePanelOpen} onOpenChange={setBrowsePanelOpen}>
                  <DrawerContent className="max-h-[85vh]">
                    <DrawerHeader className="pb-2">
                      <DrawerTitle>Browse Categories</DrawerTitle>
                    </DrawerHeader>
                    <div className="overflow-y-auto">{pickerBody}</div>
                  </DrawerContent>
                </Drawer>
              );
            }

            return browsePanelOpen ? (
              <div className="relative w-56 shrink-0 border-r overflow-y-auto">
                {pickerBody}
              </div>
            ) : null;
          })()}

          <div className="flex-1 min-w-0">
            <CardContent className={effectiveViewMode === 'list' ? 'p-0' : undefined}>
              <DataTableWrapper
                isLoading={itemsQuery.isLoading}
                isEmpty={!filteredItems || filteredItems.length === 0}
                emptyState={{
                  title: 'No line items found',
                  description:
                    search || filterBuilder.hasActiveFilters || selectedBrowseCategory
                      ? 'No items match your search or filters.'
                      : 'No line items for this supplier yet.',
                  icon: <Package className="h-12 w-12 text-muted-foreground" />,
                  action: !search && !filterBuilder.hasActiveFilters && !selectedBrowseCategory ? (
                    <Button onClick={() => navigate(createHref)} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Line Item
                    </Button>
                  ) : undefined,
                }}
              >
                {effectiveViewMode === 'list' ? (
                  <Table className="table-fixed w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12 hidden sm:table-cell overflow-hidden" aria-label="Image" />
                        <TableHead className="max-w-0 w-full">Title / Description</TableHead>
                        <TableHead className="hidden sm:table-cell w-32 overflow-hidden">Unit</TableHead>
                        <TableHead className="w-40 overflow-hidden">Last Seen Price</TableHead>
                        <TableHead className="hidden lg:table-cell w-44 overflow-hidden">Classification</TableHead>
                        <TableHead className="hidden lg:table-cell w-24 overflow-hidden">Type</TableHead>
                        <TableHead className="text-right w-20 overflow-hidden">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredItems.map((item: any) => {
                        const getItemActions = () => [
                          { label: 'View', icon: Eye, onClick: () => navigate(`/cost-center/internal-line-items/edit/${item.id}`) },
                          { label: 'Edit', icon: Edit, onClick: () => navigate(`/cost-center/internal-line-items/edit/${item.id}`) },
                          {
                            label: 'Delete',
                            icon: Trash2,
                            onClick: () => handleDelete(item.id),
                            destructive: true,
                            requiresConfirmation: {
                              title: 'Delete Line Item?',
                              description: `Are you sure you want to delete "${item.title || 'this item'}"? This action cannot be undone.`,
                            },
                          },
                        ];
                        return (
                          <TableRow
                            key={item.id}
                            className="cursor-pointer hover:bg-muted/60"
                            onClick={(e) => {
                              if ((e.target as HTMLElement).closest('button, a')) return;
                              setSelectedItem(item);
                            }}
                          >
                            <TableCell className="w-12 hidden sm:table-cell overflow-hidden">
                              {item.image_urls && item.image_urls.length > 0 ? (
                                <img
                                  src={item.image_urls[0]}
                                  alt={item.title || 'Line item'}
                                  className="h-8 w-8 rounded object-cover"
                                />
                              ) : (
                                <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="max-w-0 w-full">
                              <div className="min-w-0">
                                <span className="font-medium block truncate">
                                  {item.title ?? item.description ?? 'No title'}
                                </span>
                                <p className="text-sm text-muted-foreground truncate">
                                  {item.description || 'No description'}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell w-32 overflow-hidden">
                              <span className="block truncate text-sm">
                                {getUnitLabel(item.base_unit || 'item')}
                              </span>
                            </TableCell>
                            <TableCell className="w-40 overflow-hidden">
                              <div className="truncate">
                                {item.last_seen_price != null ? (
                                  <span>
                                    Last seen: {formatCurrency(item.last_seen_price)}
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground italic text-xs">N/A</span>
                                )}
                              </div>
                              {item.last_checked_at && (
                                <div className="text-[10px] mt-0.5 truncate text-muted-foreground">
                                  Checked {formatDistanceToNow(new Date(item.last_checked_at), { addSuffix: true })}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell w-44 overflow-hidden">
                              <div className="truncate">
                                {item.category_name ? (
                                  <Badge variant="outline" className="text-xs">{item.category_name}</Badge>
                                ) : (
                                  <span className="text-muted-foreground text-xs">—</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell w-24 overflow-hidden">
                              <Badge variant="secondary" className="text-xs">Internal</Badge>
                            </TableCell>
                            <TableCell className="text-right w-20 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                              <EntityActions actions={getItemActions()} size="sm" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 p-6">
                    {filteredItems.map((item: any) => {
                      const getItemActions = () => [
                        { label: 'View', icon: Eye, onClick: () => navigate(`/cost-center/internal-line-items/edit/${item.id}`) },
                        { label: 'Edit', icon: Edit, onClick: () => navigate(`/cost-center/internal-line-items/edit/${item.id}`) },
                        {
                          label: 'Delete',
                          icon: Trash2,
                          onClick: () => handleDelete(item.id),
                          destructive: true,
                          requiresConfirmation: {
                            title: 'Delete Line Item?',
                            description: `Are you sure you want to delete "${item.title || 'this item'}"? This action cannot be undone.`,
                          },
                        },
                      ];
                      return (
                        <Card
                          key={item.id}
                          className="hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('button, a')) return;
                            setSelectedItem(item);
                          }}
                        >
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 min-w-0">
                                  <h3 className="font-semibold truncate">
                                    {item.title ?? item.description ?? 'Untitled'}
                                  </h3>
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                    Internal
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                  {item.description}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <EntityActions actions={getItemActions()} size="sm" />
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="space-y-3">
                              <div>
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-muted-foreground">
                                    {getUnitLabel(item.base_unit || 'item')}
                                  </span>
                                  <span className="font-medium">
                                    {item.last_seen_price != null ? (
                                      <>Last seen: {formatCurrency(item.last_seen_price)}</>
                                    ) : (
                                      <span className="text-muted-foreground italic">Not calculated</span>
                                    )}
                                  </span>
                                </div>
                                {item.last_checked_at && (
                                  <div className="text-[10px] text-right mt-0.5 text-muted-foreground">
                                    Checked {formatDistanceToNow(new Date(item.last_checked_at), { addSuffix: true })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </DataTableWrapper>
            </CardContent>
          </div>
        </div>
      </Card>
      <LineItemDetailsDialog
        selectedItem={
          selectedItem
            ? {
                ...selectedItem,
                item_type: 'internal' as const,
              }
            : null
        }
        lastSeenPrice={selectedItem?.last_seen_price ?? null}
        hideMarkupSource={true}
        priceSourcesLabel="Can be found at"
        onClose={() => setSelectedItem(null)}
        onEdit={(id) => {
          setSelectedItem(null);
          navigate(`/cost-center/internal-line-items/edit/${id}`);
        }}
      />
    </PageLayout>
  );
}
